/* eslint-disable no-use-before-define */
import _ from 'underscore';
import { useMemo, useCallback, useState, useEffect, useContext } from 'react';
import { createAsyncToken } from '@bingads-webui-universal/primitive-utilities';
import { usePromisify } from '@bingads-webui-react/overreact';
import { useAccount, useCustomer, useUser, useCountries, useCurrencies, useSubGeographies } from '@bingads-webui/overreact-cc';
import { useJsDataContext } from '@bingads-webui-cc-react/js-data-rest';
import { UrlBuilderContext } from '@bingads-webui-cc-react/url-builder-react';
import RestfulService from '@bingads-webui-clientcenter/restful-service';

// higher-level helper hooks, for loading and saving account, customer & user data
// an optional fecthCondition (function or value) can be passed to control whether the data is fetched or not; by default, the data is fetched
export const useAccountFetch = (scope, fetchCondition = true) => useEntityFetch('account', scope, fetchCondition);

export const useCustomerFetch = (scope, fetchCondition = true) => useEntityFetch('customer', scope, fetchCondition);

export const useUserFetch = (scope, fetchCondition = true) => useEntityFetch('user', scope, fetchCondition);

export const useAccountPatch = scope => useEntityPatch('account', scope);

export const useCustomerPatch = scope => useEntityPatch('customer', scope);

export const useUserPatch = scope => useEntityPatch('user', scope);

export const useAcquisitionCouponRedeem = (scope, { currency, greenId }) => {
  const associateAndRedeemCoupon = useAssociateAndRedeemCouponRestCall(scope);
  const acquisitionCouponFactory = useCallback(({ website, couponCode, codeType }) => associateAndRedeemCoupon({
    currencyCode: currency,
    gid: greenId,
    campaignUrl: website,
    cco: codeType === 'cco' || codeType === 'code' ? couponCode : undefined,
    ccl: codeType === 'ccl' || codeType === 'class' ? couponCode : undefined,
  }), [currency, greenId, associateAndRedeemCoupon]);

  return acquisitionCouponFactory;
};

// relying on super-low level, low-quality API, because nothing else is compatible, JS Data, Overreact etc... just wonderful
function useAssociateAndRedeemCouponRestCall(scope) {
  const { urlBuilders } = useContext(UrlBuilderContext);
  const restfulService = useRestfulService(scope);
  return useCallback(({
    currencyCode,
    gid,
    campaignUrl,
    cco,
    ccl,
  }) => {
    const autoPbcUrl = new urlBuilders.BaseUrlBuilder('api/coupon').action('smartautocoupon').url;

    return restfulService.post(autoPbcUrl, {
      customerId: scope.cid,
      accountId: scope.aid,
      campaignUrl,
      gid,
      currencyCode,
      cco,
      ccl,
    });
  }, [restfulService, scope.aid, scope.cid, urlBuilders.BaseUrlBuilder]);
}

export const useAcquisitionCouponAssociate = (scope, { currency, greenId }) => {
  const associateCouponCall = useAssociateCouponRestCall(scope);
  const associateCouponFactory = useCallback(({ website, couponCode, codeType }) => associateCouponCall({
    currencyCode: currency,
    gid: greenId,
    campaignUrl: website,
    ccl: codeType === 'ccl' || codeType === 'class' ? couponCode : undefined,
  }), [currency, greenId, associateCouponCall]);

  return associateCouponFactory;
};

function useAssociateCouponRestCall(scope) {
  const { urlBuilders } = useContext(UrlBuilderContext);
  const restfulService = useRestfulService(scope);
  return useCallback(({
    currencyCode,
    gid,
    campaignUrl,
    ccl,
  }) => {
    const associateCouponUrl = new urlBuilders.BaseUrlBuilder('api/coupon').action('AssociateCoupon').url;

    return restfulService.post(associateCouponUrl, {
      customerId: scope.cid,
      accountId: scope.aid,
      campaignUrl,
      gid,
      currencyCode,
      ccl,
    });
  }, [restfulService, scope.aid, scope.cid, urlBuilders.BaseUrlBuilder]);
}

function useRestfulService(scope) {
  const restfulService = useMemo(
    () => new RestfulService({
      cidFetcher: _.constant(scope.cid),
      uidFetcher: _.constant(scope.uid),
      willSupportCors: true,
    }),
    [scope.cid, scope.uid]
  );

  return restfulService;
}

// NOTE: we have to use an alternative implementation here, as this API is not supported by cc overreact
// TODO: maybe update cc overreact to fully support the PI API!?
export const usePaymentInstrumentsFetch = (scope, fetchCondition = true) => {
  const ccAPI = useJsDataContext();
  const { factory: paymentInstrumentsFactory } = useLoadingDecorator(useCallback(() => ccAPI.PaymentInstrument.findAll({ accountId: scope.aid }), [ccAPI, scope]));
  const [data, setData] = useState();
  const [error, setError] = useState();
  const shouldFetch = evaluate(fetchCondition);

  useEffect(() => {
    // clean up state before every fetch cycle
    setData();
    setError();

    if (shouldFetch) {
      paymentInstrumentsFactory().then(setData, setError);
    }
  }, [shouldFetch, paymentInstrumentsFactory]);

  return { data, error };
};

export const useCountriesFetch = (scope, fetchCondition = true) => useEntityFetch('countries', scope, fetchCondition);

export const useCurrenciesFetch = (scope, fetchCondition = true) => useEntityFetch('currencies', scope, fetchCondition);

export const useProvinceFetch = (scope, countryCode, fetchCondition = true) => useEntityFetch('provinces', scope, fetchCondition, { countryCode });

// TODO: clean up! the "loading" behavior seems broken if the same factory is used to get multiple promises at the same time...
export function useLoadingDecorator(promiseFactory) {
  const [token, setToken] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const promiseFactoryCallback = useCallback((...args) => {
    const promise = promiseFactory(...args);

    setIsLoading(true);
    setToken(createAsyncToken(promise).on({
      onSuccess: () => setIsLoading(false),
      onError: () => setIsLoading(false),
    }));

    return promise;
  }, [setIsLoading, setToken, promiseFactory]);

  useEffect(() => (token ? () => token.cancel() : _.noop), [token]);

  return {
    factory: promiseFactoryCallback,
    isLoading,
  };
}

const EntityHook = {
  account: useAccount,
  customer: useCustomer,
  user: useUser,
  countries: useCountries,
  currencies: useCurrencies,
  provinces: useSubGeographies,
};

function useEntityFetch(entity, scope, fetchCondition, params) {
  return EntityHook[entity](getScopedLocator(scope, entity, params), postponeRead(!evaluate(fetchCondition)))[0];
}

function evaluate(fetchCondition) {
  return _.isFunction(fetchCondition) ? fetchCondition() : fetchCondition;
}

const SkipFetching = postponeRead(true);
function useEntityPatch(entity, scope) {
  const locator = useMemo(() => getScopedLocator(scope, entity), [scope, entity]);
  // NOTE: EntityHooks, as the name suggests, are hooks, and must always be called at the top level
  const { update } = EntityHook[entity](locator, SkipFetching)[1];
  const updateArgs = useMemo(() => [update], [update]);
  const promisifiedUpdate = usePromisify(updateArgs)[0];

  return promisifiedUpdate;
}

function getScopedLocator(scope, type, params = {}) {
  const locator = {
    ...params,
    locator: {
      descriptor: {
        uid: scope.uid,
        cid: scope.cid,
      },
    },
  };

  if (type === 'account') {
    locator.locator.descriptor.aid = scope.aid;
  }

  return locator;
}

function postponeRead(postpone) {
  return {
    fetch: {
      postponeRead: postpone,
    },
  };
}
